import * as React from 'react';

import { useStateWithDeps } from '@gitbook/util-react-hooks';

import type { PaginationProps } from './Pagination';

export interface UsePaginatedListProps<Item> {
    /** Label for the list of items */
    label?: string;

    /** The list of all items */
    items: Item[];

    /** Number of items per page to render */
    itemsPerPage: number;
}

export interface UsePaginatedList<Item, ListElement extends HTMLElement = HTMLDivElement> {
    /** DOM reference to attach to the list */
    ref: React.RefObject<ListElement>;

    /** Items to render in the current page */
    items: Item[];

    /** Props to pass to the Pagination component to handle pagination. */
    paginationProps: PaginationProps;
}

/**
 * Hook to manage a paginated list of items.
 */
export function usePaginatedList<Item = any, ListElement extends HTMLElement = HTMLDivElement>({
    label,
    items,
    itemsPerPage,
}: UsePaginatedListProps<Item>): UsePaginatedList<Item, ListElement> {
    const ref = React.useRef<ListElement>(null);
    const [page, setPage] = useStateWithDeps(0, [items]);
    const mountedRef = React.useRef(false);

    // Detect the scroll parent and scroll to the top when the page changes
    React.useEffect(() => {
        if (ref.current && mountedRef.current) {
            ref.current.scrollIntoView({ behavior: 'smooth' });
        }

        mountedRef.current = true;
    }, [page, mountedRef]);

    return React.useMemo(() => {
        const totalItems = items.length;
        const maxPages = Math.ceil(totalItems / itemsPerPage) - 1;
        const pagedData = items.slice(
            page * itemsPerPage,
            Math.min((page + 1) * itemsPerPage, totalItems)
        );
        const hasPrevious = page > 0;
        const hasNext = page < maxPages;

        return {
            ref,
            items: pagedData,
            paginationProps: {
                range: {
                    label,
                    totalItems,
                    itemsPerPage,
                    page,
                },
                previous: hasPrevious ? () => setPage(page - 1) : undefined,
                next: hasNext ? () => setPage(page + 1) : undefined,
            },
        };
    }, [ref, label, items, itemsPerPage, page]);
}
